import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";
import { getCategoryById } from "@/mocks/categories";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "horizontal" | "compact";
}

export default function ArticleCard({ article, variant = "default" }: ArticleCardProps) {
  const category = getCategoryById(article.categoryId);

  if (variant === "horizontal") {
    return (
      <article className="group flex gap-4 p-4 rounded-lg bg-background-50 hover:bg-background-100 transition-all duration-300 cursor-pointer" data-product-shop="">
        <Link to={`/article/${article.id}`} className="flex-shrink-0 w-40 h-28 overflow-hidden rounded-lg">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </Link>
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              {category && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-600 font-medium whitespace-nowrap">
                  {category.name}
                </span>
              )}
              {article.isBreaking && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium whitespace-nowrap">
                  重磅
                </span>
              )}
            </div>
            <Link to={`/article/${article.id}`}>
              <h3 className="text-sm font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
                {article.title}
              </h3>
            </Link>
            <p className="text-xs text-foreground-500 mt-1 line-clamp-1">{article.summary}</p>
          </div>
          <div className="flex items-center gap-3 text-xs text-foreground-400 mt-2">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line w-3 h-3 flex items-center justify-center"></i>
              {article.viewCount > 10000 ? `${(article.viewCount / 10000).toFixed(1)}万` : article.viewCount}
            </span>
            <span>{article.publishDate}</span>
          </div>
        </div>
      </article>
    );
  }

  if (variant === "compact") {
    return (
      <article className="group flex items-start gap-3 py-3 border-b border-background-200 last:border-0 cursor-pointer" data-product-shop="">
        <Link to={`/article/${article.id}`} className="flex-shrink-0 w-20 h-16 overflow-hidden rounded-md">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </Link>
        <div className="flex-1 min-w-0">
          <Link to={`/article/${article.id}`}>
            <h3 className="text-sm font-medium text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
              {article.title}
            </h3>
          </Link>
          <div className="flex items-center gap-2 text-xs text-foreground-400 mt-1">
            <span>{article.publishDate}</span>
            <span>·</span>
            <span>{article.viewCount > 10000 ? `${(article.viewCount / 10000).toFixed(1)}万阅` : `${article.viewCount}阅`}</span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col rounded-lg overflow-hidden bg-background-50 hover:shadow-md transition-all duration-300 cursor-pointer" data-product-shop="">
      <Link to={`/article/${article.id}`} className="relative w-full h-48 overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute top-3 left-3 flex items-center gap-1.5 flex-wrap">
          {category && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-primary-500 text-background-50 font-medium whitespace-nowrap">
              {category.name}
            </span>
          )}
          {article.isBreaking && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-accent-500 text-background-50 font-medium whitespace-nowrap">
              重磅
            </span>
          )}
        </div>
        {article.isHot && (
          <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-accent-500 text-background-50 text-xs font-bold">
            热
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
      </Link>
      <div className="p-4 flex flex-col flex-1">
        <Link to={`/article/${article.id}`}>
          <h3 className="text-base font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug mb-2">
            {article.title}
          </h3>
        </Link>
        <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed flex-1">{article.summary}</p>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-200">
          <div className="flex items-center gap-3 text-xs text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line w-3 h-3 flex items-center justify-center"></i>
              {article.viewCount > 10000 ? `${(article.viewCount / 10000).toFixed(1)}万` : article.viewCount}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line w-3 h-3 flex items-center justify-center"></i>
              {article.publishDate}
            </span>
          </div>
          <Link
            to={`/article/${article.id}`}
            className="text-xs text-primary-500 font-medium hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            阅读全文 →
          </Link>
        </div>
      </div>
    </article>
  );
}